import styled from '@emotion/styled';
import { Currency, PerformanceValueType } from '@kaizenplatform/hugot-entity';
import { Paper, useMediaQuery } from '@material-ui/core';
import { createElement, FC, ReactText } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

import { graphColorsPalette } from '../../../constant/color';
import { SPACING_HUGE } from '../../../constant/spacing';
import { breakpoint } from '../../../styleUtility/mediaquery';
import RechartsResponsiveContainer from '../../primitive/RechartsResponsiveContainer';

type Fragment = {
  date?: string;
  type?: PerformanceValueType;
  currency?: Currency;
  video_view_rate_p25?: number;
  video_view_rate_p50?: number;
  video_view_rate_p75?: number;
  video_view_rate_p95?: number;
  video_view_rate_p100?: number;
};

type GraphDataItem = Record<string, string | number> & Fragment;

export type GraphData = GraphDataItem[];

type Props = { data: GraphData; isViewRate: boolean; className?: string } & InjectedIntlProps;

const DailySummaryPerformanceGraph: FC<Props> = ({ intl, data, isViewRate, className }) => {
  const isSmartPhoneScreenWidth = useMediaQuery(`(max-width:${breakpoint.sp}px)`);
  const marginRight = isSmartPhoneScreenWidth ? 20 : 40;

  const { type, currency } = data[0];
  const xAxisKey = isViewRate ? 'percent' : 'date';

  const keys = Object.keys(data[0]);
  const formatKeys = keys.filter((key) => !['date', 'type', 'currency', 'percent'].includes(key));

  const renderCreativeId = (id: string) => `ID: ${id}`;

  const formatValue = (value: number) => {
    return currency && type
      ? intl.formatNumber(value, {
          style: 'currency',
          maximumFractionDigits: 2,
          currency: currency,
          currencyDisplay: 'symbol',
        })
      : intl.formatNumber(value, { style: 'percent', maximumFractionDigits: 2 });
  };

  const formatLabel = (value: ReactText) => {
    return isViewRate ? intl.formatMessage({ id: `performance_report_metric.${value}` }) : value;
  };

  return (
    <Root className={className}>
      <RechartsResponsiveContainer width="100%" height={350}>
        <LineChart
          data={data}
          margin={{ left: 12, right: marginRight, bottom: 30 }}
          style={{
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: 14,
          }}
        >
          {formatKeys.map((key: string, i: number) => (
            <Line
              dot={false}
              key={i}
              type="monotone"
              dataKey={key}
              connectNulls
              stroke={graphColorsPalette[i % graphColorsPalette.length]}
              strokeWidth={1.5}
            />
          ))}
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} tickFormatter={formatLabel} interval="preserveStartEnd" />
          <YAxis tickFormatter={formatValue} />
          <Tooltip
            formatter={(value, name) => [formatValue(value as number), renderCreativeId(name)]}
            labelFormatter={(value) => formatLabel(value)}
          />
          <Legend
            formatter={(value) => renderCreativeId(value)}
            wrapperStyle={{ left: 20, right: 20, lineHeight: 1.7 }}
            iconType="plainline"
          />
        </LineChart>
      </RechartsResponsiveContainer>
    </Root>
  );
};

const Root = styled(Paper)({
  paddingTop: SPACING_HUGE,
});

export default injectIntl(DailySummaryPerformanceGraph);
